/**
 * Layout-route guard for customer pages that require a customer login (Home,
 * Favourites, Followed Businesses, My Account, ...). Mirror image of
 * BusinessSessionGuard: anything that isn't a logged-in customer session —
 * guests AND business/admin sessions — is sent to the guest page instead of
 * mounting the screen, so no customer-only fetches fire with the wrong token.
 *
 * Customer sessions are Firebase ID tokens, which carry a `firebase` claim
 * (backend JWTs don't) — same check as AuthorizedRedirect.
 *
 * `isRestoringAuth` mirrors the check Home already uses: wait for token
 * rehydration instead of bouncing a real customer on page load.
 *
 * Usage in customerRoutes.js:
 *   <Route element={<CustomerSessionGuard />}>
 *     <Route path="/Home" element={<Home />} />
 *     ...
 *   </Route>
 */
import React from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';

export const CustomerSessionGuard = ({
  authorized,
  isCustomerSession,
  isRestoringAuth,
  redirectTo = '/',
}) => {
  if (isRestoringAuth) {
    return null;
  }
  if (!authorized || !isCustomerSession) {
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
};

CustomerSessionGuard.propTypes = {
  authorized: PropTypes.bool,
  isCustomerSession: PropTypes.bool,
  isRestoringAuth: PropTypes.bool,
  redirectTo: PropTypes.string,
};

const mapStateToProps = state => ({
  authorized: Boolean(state.auth && state.auth.authorized),
  isCustomerSession: Boolean(
    state.auth.tokens.accessDecoded && state.auth.tokens.accessDecoded.firebase,
  ),
  isRestoringAuth: Boolean(
    state.auth.persist && state.auth.persist.restore && state.auth.persist.restore.fetching,
  ),
});

export default connect(mapStateToProps)(CustomerSessionGuard);